import React, { useEffect, useState } from "react";
import { Button, Input } from "antd";
import IconTriggerModal from "../../Snippets/IconTriggerModal";

// Campo numérico de uma etapa de Criar/Atualizar Registro.
//
// Dois conteúdos são legítimos aqui: um número, digitado direto, e uma anotação
// ({{ ... }}) montada no editor do raio, que só vira número na execução. O
// antigo InputNumber só sabia lidar com o primeiro: qualquer coisa que não
// parseasse como número era descartada em silêncio, no onChange enquanto se
// digita e de novo no blur (@rc-component/input-number, collectInputValue e
// flushInputValue). Isso derrubava as duas situações do chamado — o texto
// digitado sumia sem aviso, e uma anotação salva pelo raio era apagada assim que
// o RecordForm reinjetava o valor pelo setFieldsValue.
//
// Por isso o controle é um Input comum com validação própria: o conteúdo
// recusado continua na tela junto do motivo, e a anotação é preservada.

const isAnnotation = (value) =>
    typeof value === "string" && value.includes("{{");

const toText = (value) =>
    value === null || value === undefined ? "" : String(value);

// Aceita inteiro ou decimal, com vírgula ou ponto, e sinal negativo.
const isNumericText = (text) => /^-?\d+([.,]\d+)?$/.test(text);

// Estados intermediários de digitação ("-", "12,", ","): ainda não são número,
// mas também não são erro — avisar aqui seria acusar o usuário no meio da
// palavra. Só não são gravados.
const isPartialNumericText = (text) => /^-?\d*[.,]?\d*$/.test(text);

export const NumericInput = ({ value, onChange, fieldName, fieldLabel, prefix }) => {

    const annotation = isAnnotation(value);
    const [text, setText] = useState(() => toText(value));
    const [invalid, setInvalid] = useState(false);

    // Valor que chega de fora (raio, troca de passo, recarga do config).
    // Enquanto há conteúdo recusado na tela não sobrescrevemos: é justamente o
    // texto que o usuário precisa continuar vendo ao lado do aviso.
    useEffect(() => {
        if (invalid) return;
        setText(toText(value));
    }, [value, invalid]);

    const handleChange = (e) => {
        const next = e.target.value;
        setText(next);

        if (next.trim() === "") {
            setInvalid(false);
            onChange(null);
            return;
        }

        if (isNumericText(next.trim())) {
            setInvalid(false);
            onChange(Number(next.trim().replace(",", ".")));
            return;
        }

        setInvalid(!isPartialNumericText(next.trim()));
    };

    const clearAnnotation = () => {
        setText("");
        setInvalid(false);
        onChange(null);
    };

    const bolt = <IconTriggerModal fieldName={fieldName} fieldLabel={fieldLabel} />;

    if (annotation) {
        return (
            <>
                <Input
                    className="ae-input--locked"
                    value={toText(value)}
                    readOnly
                    style={{ width: "100%" }}
                    addonAfter={bolt}
                />
                <div className="ae-hint">
                    Anotação: o valor é calculado durante a execução e precisa
                    resultar em número. Edite pelo raio.
                    <Button type="link" size="small" onClick={clearAnnotation} style={{ padding: "0 4px" }}>
                        Usar um número
                    </Button>
                </div>
            </>
        );
    }

    return (
        <>
            <Input
                value={text}
                onChange={handleChange}
                prefix={prefix || null}
                placeholder="Somente números"
                inputMode="decimal"
                status={invalid ? "error" : undefined}
                style={{ width: "100%" }}
                addonAfter={bolt}
            />
            <div className={invalid ? "ae-hint ae-hint--error" : "ae-hint"}>
                {invalid
                    ? "Este campo aceita somente números. Para usar uma anotação, monte o valor no editor do raio."
                    : "Somente números. Para usar uma anotação, monte o valor no editor do raio."}
            </div>
        </>
    );
};
